/**
 * Apply a project's customization (colours, radius, font, favicon, copy) to
 * this page. The values arrive in `snapshot.branding`, resolved server-side
 * from the project (`verif::customization`) — never from `context`, which an
 * API caller can write.
 *
 * Everything here is reversible: calling it again with `null`s restores the
 * theme.css defaults and the bundled copy, so the console preview can switch
 * settings back and forth on one page.
 */
import type { i18n as I18n } from 'i18next';

import { apiUrl } from './client';

import type { TenantStyle } from './types';

/**
 * Colour role → the theme.css custom properties it drives. Roles mirror the
 * server's `COLOR_ROLES`; a role the server adds that is missing here is
 * simply not applied.
 */
const ROLE_VARS: Record<string, readonly string[]> = {
  headers: ['--title', '--foreground'],
  paragraphs: ['--text'],
  support_texts: ['--muted-text'],
  background: ['--background'],
  pills: ['--pill'],
  pills_text: ['--pill-foreground'],
  primary_button: ['--primary', '--ring'],
  primary_button_text: ['--primary-foreground'],
  selector_controls: ['--selector'],
  secondary_button: ['--secondary'],
  secondary_button_text: ['--secondary-foreground'],
  progress_bar: ['--progress'],
};

/** Everything this module may set, so a re-apply can clear what it set before. */
const MANAGED_VARS = [
  ...new Set([
    ...Object.values(ROLE_VARS).flat(),
    '--primary-pressed',
    '--primary-active',
    '--radius-card',
    '--radius-button',
    '--radius-option',
    '--font-sans',
  ]),
];

/** Fonts the server allows (`verif::customization::FONTS`). The URL is built
 * from this list only, so a tampered value cannot point the page elsewhere. */
const FONTS = new Set([
  'Sarabun',
  'Inter',
  'Noto Sans Thai',
  'IBM Plex Sans Thai',
  'Prompt',
  'Kanit',
  'Anuphan',
  'Bai Jamjuree',
]);

const COLOR = /^#[\da-f]{6}(?:[\da-f]{2})?$/i;

/**
 * The shade a pressed / active primary button takes, given the colour the
 * project chose for it. theme.css hand-picks two darker purples for the
 * default; a project colour gets a rule instead: the same colour mixed toward
 * black, so any hue darkens a step and a translucent `#RRGGBBAA` keeps its alpha.
 */
export function pressedShades(primary: string): [string, string] {
  return [
    `color-mix(in srgb, ${primary} 88%, black)`,
    `color-mix(in srgb, ${primary} 78%, black)`,
  ];
}

/** The CSS custom properties a style sets. Pure, for tests. */
export function cssVarsFor(style: TenantStyle | null | undefined): Record<string, string> {
  const vars: Record<string, string> = {};
  if (!style) return vars;
  for (const [role, value] of Object.entries(style.colors ?? {})) {
    const targets = ROLE_VARS[role];
    if (!targets || !COLOR.test(value)) continue;
    for (const name of targets) vars[name] = value;
  }
  const primary = style.colors?.primary_button;
  if (primary && COLOR.test(primary)) {
    const [pressed, active] = pressedShades(primary);
    vars['--primary-pressed'] = pressed;
    vars['--primary-active'] = active;
  }
  if (typeof style.radius === 'number' && style.radius >= 0 && style.radius <= 28) {
    // Buttons take the chosen radius; cards stay a step rounder, as in theme.css.
    vars['--radius-button'] = `${style.radius}px`;
    vars['--radius-option'] = `${style.radius}px`;
    vars['--radius-card'] = `${Math.min(style.radius + 4, 32)}px`;
  }
  if (style.font && FONTS.has(style.font)) {
    vars['--font-sans'] = `'${style.font}', system-ui, -apple-system, 'Segoe UI', sans-serif`;
  }
  return vars;
}

const FONT_LINK_ID = 'trustsy-custom-font';

function applyFont(font: string | null | undefined): void {
  const existing = document.getElementById(FONT_LINK_ID);
  if (!font || !FONTS.has(font) || font === 'Sarabun') {
    existing?.remove();
    return;
  }
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font).replaceAll('%20', '+')}:wght@400;500;600;700&display=swap`;
  if (existing instanceof HTMLLinkElement) {
    if (existing.href !== href) existing.href = href;
    return;
  }
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.append(link);
}

/** Our own brand-asset path only — the same rule the consent logo follows. */
const BRAND_ASSET_PATH = /^\/api\/v1\/brand-assets\/[\w-]+$/;
let defaultFavicon: string | null = null;

function applyFavicon(url: string | null | undefined): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!link) return;
  defaultFavicon ??= link.href;
  link.href = url && BRAND_ASSET_PATH.test(url) ? apiUrl(url) : defaultFavicon;
}

/** Bundled copy for every key a project overrode, so an override can be undone. */
const bundled = new Map<string, string>();

/**
 * Replace copy keys with the project's text, per language. `{appName}` becomes
 * the display name; `{{` is stripped so a project's text cannot reach other
 * interpolation values. React renders the result as text, never HTML.
 */
export function applyTexts(
  i18n: I18n,
  texts: Record<string, Record<string, string>> | null | undefined,
  appName: string | null | undefined,
): void {
  // Restore everything overridden last time, then lay the new set on top.
  for (const [id, value] of bundled) {
    const [lng, key] = splitId(id);
    i18n.addResource(lng, 'translation', key, value);
  }
  bundled.clear();
  for (const [lng, entries] of Object.entries(texts ?? {})) {
    for (const [key, raw] of Object.entries(entries)) {
      const current = i18n.getResource(lng, 'translation', key) as unknown;
      if (typeof current !== 'string' || !raw.trim()) continue;
      bundled.set(`${lng}\u0000${key}`, current);
      const text = raw
        .replaceAll('{{', '')
        .replaceAll('}}', '')
        .replaceAll('{appName}', appName ?? '');
      i18n.addResource(lng, 'translation', key, text);
    }
  }
  // Re-render with the new strings.
  void i18n.changeLanguage(i18n.language);
}

function splitId(id: string): [string, string] {
  const at = id.indexOf('\u0000');
  return [id.slice(0, at), id.slice(at + 1)];
}

export interface CustomizationInput {
  style?: TenantStyle | null;
  texts?: Record<string, Record<string, string>> | null;
  favicon_url?: string | null;
  display_name: string | null;
}

/** Apply (or, with nulls, undo) a project's customization on this page. */
export function applyCustomization(branding: CustomizationInput, i18n: I18n): void {
  const root = document.documentElement.style;
  for (const name of MANAGED_VARS) root.removeProperty(name);
  for (const [name, value] of Object.entries(cssVarsFor(branding.style))) {
    root.setProperty(name, value);
  }
  applyFont(branding.style?.font);
  applyFavicon(branding.favicon_url);
  applyTexts(i18n, branding.texts, branding.display_name);
}
